import { Form } from 'antd';
import { AppButton } from 'shared/UI/UIKit/Button';
import { AppFlex } from 'shared/UI/UIKit/Flex';
import { AppForm, AppFormItem, formRules } from 'shared/UI/UIKit/Form';
import { AppInput } from 'shared/UI/UIKit/Input';
import { AppModal } from 'shared/UI/UIKit/Modal';
import { AppSelect } from 'shared/UI/UIKit/Select';
import { useCreateClassGroupMutation, useGetAvailableClassGroupTeachersQuery } from 'shared/api';
import type { AppSelectOption } from 'shared/UI/UIKit/Select';
import type { CreateClassGroupBody } from 'shared/api';

type CreateClassGroupModalProps = {
  visible: boolean;
  classOptions: AppSelectOption[];
  subjectOptions: AppSelectOption[];
  onClose: () => void;
};

type CreateClassGroupFormValues = Omit<CreateClassGroupBody, 'name'> & {
  name: string;
};

const requiredSelectRule = [{ required: true, message: 'Обязательное поле' }];

export const CreateClassGroupModal = ({
  visible,
  classOptions,
  subjectOptions,
  onClose,
}: CreateClassGroupModalProps) => {
  const [form] = Form.useForm<CreateClassGroupFormValues>();
  const educationalClassId = Form.useWatch('educationalClassId', form);
  const subjectId = Form.useWatch('subjectId', form);
  const [createClassGroup, { isLoading }] = useCreateClassGroupMutation();
  const { data: teachers = [], isFetching: teachersLoading } =
    useGetAvailableClassGroupTeachersQuery(
      { educationalClassId: educationalClassId || 0, subjectId: subjectId || 0 },
      { skip: !visible || !educationalClassId || !subjectId },
    );

  const close = () => {
    form.resetFields();
    onClose();
  };

  const onSubmit = async () => {
    try {
      const values = await form.validateFields();
      const name = values.name.trim();

      await createClassGroup({ ...values, name }).unwrap();
      close();
    } catch {
      // Ошибки валидации отображаются рядом с полями, ошибки API — общим baseQuery.
    }
  };

  const footer = (
    <AppFlex direction="row">
      <AppButton onClick={onSubmit}>Создать</AppButton>
      <AppButton type="outline" onClick={close}>
        Отмена
      </AppButton>
    </AppFlex>
  );

  return (
    <AppModal
      visible={visible}
      maskClosable={false}
      onCancel={close}
      destroyOnClose
      loading={isLoading}
      title="Создать учебную группу"
      footer={footer}
      content={
        <AppForm
          form={form}
          onValuesChange={(changedValues) => {
            if ('educationalClassId' in changedValues || 'subjectId' in changedValues) {
              form.setFieldsValue({ teacherId: undefined });
            }
          }}
        >
          <AppFormItem name="educationalClassId" label="Класс" rules={requiredSelectRule}>
            <AppSelect
              showSearch
              optionFilterProp="label"
              options={classOptions}
              placeholder="Выберите класс"
            />
          </AppFormItem>
          <AppFormItem name="subjectId" label="Предмет" rules={requiredSelectRule}>
            <AppSelect
              showSearch
              optionFilterProp="label"
              options={subjectOptions}
              placeholder="Выберите предмет"
            />
          </AppFormItem>
          <AppFormItem name="teacherId" label="Преподаватель">
            <AppSelect
              showSearch
              optionFilterProp="label"
              disabled={!educationalClassId || !subjectId}
              loading={teachersLoading}
              options={teachers.map((teacher) => ({ label: teacher.code, value: teacher.id }))}
              placeholder="Выберите преподавателя"
            />
          </AppFormItem>
          <AppFormItem name="name" label="Название" rules={formRules.required}>
            <AppInput maxLength={255} placeholder="Введите название группы" />
          </AppFormItem>
        </AppForm>
      }
    />
  );
};
